import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self, SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { GasDigitsMaskHelper, GasMathHelper } from '@cikrf/gas-utils/helpers';

import { startWith, tap } from 'rxjs/operators';
import { parse } from 'date-fns';

import { GasIcon } from '../../../utils/gas-icons/icon/gas-icon.namespace';
import { GasColor } from '../../../core/colors/color.namespace';
import { GasIconColor, GasIconCursor, GasInputElement } from '../gas-input.interface';
import { GasFormBaseComponent } from '../../gas-form-base-component';
import { GasDateDirective } from '../../../utils/gas-date-directive/gas-date.directive';
import { GasForm } from '../../gas-form.namespace';
import { GasInputFieldComponent } from '../field/gas-input-field.component';
import { checkCurrentTime } from '../../../utils/gas-datepicker-directive/datepicker.helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const TEXTAREA_LENGTH = 30;
const ICON_INFO: Record<GasIconColor, GasColor.IconColorClass> = {
  ['brand']: GasColor.IconColorClass.Brand,
  ['default']: GasColor.IconColorClass.Brand,
  ['black']: GasColor.IconColorClass.Black,
  ['error']: GasColor.IconColorClass.Error,
};

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=589%3A5592 */
@UntilDestroy()
@Component({
  selector: 'gas-input',
  templateUrl: './gas-input.component.html',
  styleUrls: ['./gas-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasInputComponent extends GasFormBaseComponent implements OnInit, OnChanges {
  /** Текст ошибки */
  @Input()
  public error: string;

  /** Лейбл */
  @Input()
  public label: string;

  /** Текст плейсхолдера */
  @Input()
  public placeholder = '';

  @Input()
  public value = '';

  /** Скрывает все подсказки и тултипы */
  @GasInputBoolean()
  @Input()
  public hide: string | boolean;

  /** Иконка слева */
  @Input()
  public suffixIcon: GasIcon.Icons | string;

  /** Цвет иконки слева */
  @Input()
  public suffixIconColor: GasIconColor | string = 'default';

  /** Иконка справа */
  @Input()
  public postfixIcon: GasIcon.Icons | string;

  /** Цвет иконки справа */
  @Input()
  public postfixIconColor: GasIconColor | string = 'default';

  @Input()
  public postfixIconCursor: GasIconCursor | string = 'default';

  /** Тип нативного инпута */
  @Input()
  public nativeType: 'text' | 'password' | 'number' | string = 'text';

  /** Id инпута */
  @Input()
  public nativeId: string = GasMathHelper.getUniqueId();

  /** Тип нативного элемента */
  @Input()
  public nativeElement: GasInputElement = 'input';

  /** Минимальное значение нативного number инпута */
  @Input('min')
  public nativeNumberMin: number;

  /** Максимальное значение нативного number инпута */
  @Input('max')
  public nativeNumberMax: number;

  /** Максимальное число символов ввода */
  @Input()
  public maxLength: number;

  /** Флаг о включении иконки информации у лейбла */
  @GasInputBoolean()
  @Input()
  public infoIcon: string | boolean = false;

  /** Маска */
  @Input()
  public digitsMask: string;

  /** Возможность очистки значения в инпуте */
  @Input()
  public clearable = false;

  /** Флаг, который указывает, что в инпут вводится время */
  @Input()
  public timeInput = false;

  /** @deprecated */
  @Input()
  public fieldLevelHelpTooltip: string;

  @Output('postfixIconClicked')
  public postfixIconClicked$ = new EventEmitter();

  @Output('onBlur')
  public onBlur$ = new EventEmitter<Event>();

  @ViewChild('inputRef')
  public inputElement: GasInputFieldComponent;

  public iconInfo = ICON_INFO;

  public get isVisibilityInput(): boolean {
    return this.nativeElement === 'input';
  }

  public get isVisibilityTextarea(): boolean {
    return this.nativeElement === 'textarea';
  }

  public get showError(): boolean {
    const showError = Boolean(!this.hide && this.error);

    if (!this.ngControl) {
      return showError;
    }
    return this.ngControl?.control?.dirty && this.ngControl?.control?.invalid && showError;
  }

  // public модификаторы свойств конструктора добавлены т.к. свойства используются в других компонентах
  constructor(
    public elementRef: ElementRef,
    public changeDetectorRef: ChangeDetectorRef,
    public renderer: Renderer2,
    @Self() @Optional() public ngControl: NgControl,
    @Optional() private gasDateDirective: GasDateDirective,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    // todo: this.value в инпуте немного бажит, надо уйти от него и оставить только работу с формами
    if (this.timeInput) {
      this.value = this.form.value;
    }

    if (this.gasDateDirective) {
      this.digitsMask = this.gasDateDirective.format.replace(/[a-zA-Z]/ig, '_');
    }

    /**TODO: перенести данную реализацию в GasFormBaseComponent и перевести на нее все компоненты */
    this.ngControl?.control?.statusChanges.pipe(
      untilDestroyed(this),
      startWith(this.ngControl.control.status),
      tap(() => {
        this.changeDetectorRef.markForCheck();
      }),
    ).subscribe((status: GasForm.States) => this.nativeDisabled = status === GasForm.States.Disabled);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // todo: нужно тотально переделать компонент
    if (changes.value?.currentValue) {
      this.changeDetectorRef.detectChanges();
      if (this.form.value !== this.value) {
        this.form.setValue(this.value, { onlySelf: true, emitEvent: false });
      }
    }

    if (!this.digitsMask && !this.gasDateDirective) {
      this.value = this.form.value;
    }
  }

  public isHasSuffixIcon(): boolean {
    return Boolean(this.suffixIcon);
  }

  public isHasPostfixIcon(): boolean {
    return Boolean(this.postfixIcon) || this.clearable && this.form.value?.length;
  }

  public isInfo(): boolean {
    return Boolean(this.label) && this.infoIcon as boolean;
  }

  public getTextareaCols(): number {
    return this.maxLength && this.maxLength > TEXTAREA_LENGTH ? this.maxLength : TEXTAREA_LENGTH;
  }

  public isMaxLengthPromptView(): boolean {
    return this.maxLength &&
      !this.error &&
      this.nativeElement === 'textarea';
  }

  public postfixIconClick(): void {
    if (this.clearable) {
      this.value = '';
      this.writeValue('');
      return;
    }

    this.postfixIconClicked$.emit();
  }

  public checkValueForMask(event: Event): void {
    /** есть маска */
    if (this.digitsMask) {
      (event.target as HTMLInputElement).value = GasDigitsMaskHelper.mask((event.target as HTMLInputElement).value, this.digitsMask);
      this.writeValue((event.target as HTMLInputElement).value);

      this.setSelectionPosition();
      this.changeDetectorRef.detectChanges();
    }

    /** если работаем с датапикером - преобразуем значение в дату */
    if (this.gasDateDirective) {
      if (this.form.value.length === this.gasDateDirective.format.length) {
        const date = parse(this.form.value, this.gasDateDirective.format, new Date());

        if (date?.getTime()) {
          this.onChange(date);
          this.checkDateInputError(null);
        } else {
          this.error = this.error ? this.error : 'Введена некорректная дата';
          this.checkDateInputError(this.error);
        }
      } else {
        this.error = '';
        this.checkDateInputError(null);
        this.state = GasForm.InputState.Default;
      }
    }
  }

  public blur(event: Event): void{
    this.onBlur$.emit(event);
  }

  private setSelectionPosition(): void {
    if (!this.form.value) {
      return;
    }

    const valueLength = this.form.value?.length || 0;

    /** если инпут используется для ввода времени, то проверяем корректность ввода */
    if (this.timeInput) {
      this.value = checkCurrentTime(this.form.value.split(':')) + this.digitsMask.slice(valueLength);
    } else {
      this.value = this.form.value + this.digitsMask.slice(valueLength);
    }

    //нужна нулевая задержка, что бы курсор мог правильно сдвинуться на указанную позицию
    setTimeout(() => {
      this.inputElement.elementRef.nativeElement.selectionStart =
        this.inputElement.elementRef.nativeElement.selectionEnd = this.form.value?.length || 0;
    });
  }

  private checkDateInputError(error: string = ''): void {
    //eslint-disable-next-line @typescript-eslint/naming-convention
    this.form.setErrors({INPUT_DATE_ERROR: error}, {emitEvent: false});
    //eslint-disable-next-line @typescript-eslint/naming-convention
    this.ngControl?.control?.setErrors({INPUT_DATE_ERROR: error}, {emitEvent: false});

    if (!error) {
      this.form.updateValueAndValidity({emitEvent: false});
      this.ngControl?.control?.updateValueAndValidity({emitEvent: false});
    }
  }
}
